import {
  defineComponent,
  getCurrentInstance,
  h,
  inject,
  onBeforeUnmount,
  onMounted,
  provide,
  watch,
} from 'vue'
import { debounce } from 'lodash-es'
import { useGlobalConfig } from '@zoryn/components/config-provider'
import { useFormSize } from '@zoryn/components/form'
import { SIZE_INJECTION_KEY, useLocale } from '@zoryn/hooks'
import { GRID_HTTP_INJECTION_KEY } from '@zoryn/components/grid/src/constants/grid-http'
import { ensureGridRequest } from '@zoryn/components/grid/src/utils/resolve-grid-request'
import { createLoadColumns } from './load-columns'
import { gridTreeProps } from './props'
import { createInternalData, createReactData } from './store'
import { attachGridTreeMethods } from './methods'
import { resolveGridTreeOptions } from './utils/resolve-grid-tree-options'
import GridTreeLayout from './components/grid-tree-layout.vue'
import type { DataGridTree, GridColumnProps, GridTreeOptions } from './types'

export default defineComponent({
  name: 'ZGridTree',
  props: gridTreeProps,
  setup(props, { expose }) {
    useLocale()
    useGlobalConfig('lov')
    const gridSize = useFormSize()
    provide(SIZE_INJECTION_KEY, { size: gridSize })

    const options = resolveGridTreeOptions(props.options) as GridTreeOptions
    const reactData = createReactData(options)
    const internalData = createInternalData()

    const $gridTree = {
      props,
      options,
      reactData,
      internalData,
      gridSize,
      router: getCurrentInstance()?.appContext.config.globalProperties.$router,
    } as DataGridTree

    ensureGridRequest($gridTree, inject(GRID_HTTP_INJECTION_KEY, null))
    attachGridTreeMethods($gridTree)

    const debouncedAutoQuery = debounce(() => {
      if (!options.module || options.autoQuery === false) {
        return
      }
      void $gridTree.queryByExamplePage()
    }, 100)

    watch(
      () => options.params,
      () => {
        debouncedAutoQuery()
      },
      { deep: true }
    )

    const loadColumns = createLoadColumns($gridTree as never, () => undefined)
    $gridTree.loadColumns = (columns: GridColumnProps[]) => {
      void loadColumns(columns)
    }

    watch(
      () => props.options,
      (next) => {
        if (next && next !== options) {
          Object.assign(options, next)
        }
      }
    )

    watch(
      () => props.options?.columns,
      (columns) => {
        if (columns?.length) {
          void loadColumns(columns)
        }
      }
    )

    if (options.columns?.length) {
      void loadColumns(options.columns)
    }

    options.getCurrentRow = () => reactData.currentRow

    onMounted(() => {
      if (options.module && options.autoQuery !== false) {
        void $gridTree.queryByExamplePage()
      }
    })

    onBeforeUnmount(() => {
      debouncedAutoQuery.cancel()
      reactData.tableData = []
      internalData.flatSource = []
    })

    provide('$grid', $gridTree as never)

    expose({
      getCurrentRow: () => reactData.currentRow,
      queryByExamplePage: $gridTree.queryByExamplePage,
      loadTreeData: $gridTree.loadTreeData,
    })

    return () => h(GridTreeLayout)
  },
})
